import { useMemo } from "react";
import "react-quill-new/dist/quill.snow.css";
import "./TuiViewer.scss";
import { URL_REGEX, splitTrailingPunctuation } from "utils/renderWithLinks";

/*
 * 에디터 본문 뷰어.
 * ---------------------------------------------------------------------------
 * 어드민 RichTextEditor(Quill)에서 저장된 본문 HTML 을 "그대로" 렌더링한다.
 *
 * ReactQuill 컴포넌트를 readOnly 로 쓰면 Quill 이 HTML 을 자체 편집 모델로
 * 다시 파싱하면서, 화이트리스트(blot)에 등록되지 않은 서식(예: 손글씨
 * `ql-font-gaegu`, 일부 인라인 style/size)을 제거해 버린다.
 * 뷰어는 편집이 필요 없으므로 Quill 파서를 거치지 않고 저장된 HTML 을
 * 직접 주입한다. 이렇게 하면 어떤 서식도 손실 없이 렌더링된다.
 *
 * - 정렬/리스트/들여쓰기/serif·monospace 등 Quill 기본 클래스는
 *   quill.snow.css 의 `.ql-editor` 규칙이 처리한다.
 * - 한글 웹폰트와 `ql-font-*` → font-family 매핑은 TuiViewer.css 가 담당한다.
 * ---------------------------------------------------------------------------
 */

const makeHTMLWithImages = (images) => {
    return images
        .map((image) => {
            return `<img src="${image}" alt="image" />`;
        })
        .join("");
};

// Quill 은 빈 줄을 `<p><br></p>` 로 저장하지만, 직렬화 과정에서 `<br>` 이
// 빠진 `<p></p>` 로 넘어오면 라인 박스가 없어 높이 0 으로 찌그러진다.
// 빈 단락에 `<br>` 을 복원해 엔터(빈 줄)가 그대로 보이도록 한다.
const normalizeEmptyParagraphs = (html) =>
    html.replace(/<p>(\s|&nbsp;)*<\/p>/gi, "<p><br></p>");

// 에디터에서 링크 버튼을 쓰지 않고 `<p>https://...</p>` 처럼 URL 을 텍스트로만
// 입력한 경우에도 클릭할 수 있도록 <a> 로 감싼다.
// - 이미 <a> 안에 있는 텍스트는 건드리지 않는다.
// - URL 판별·끝 문장부호 처리는 plain text 용 renderWithLinks 와 동일한 규칙을 쓴다.
// - DOMParser 문서는 스크립트·이미지 로드가 일어나지 않는 비활성 문서라 파싱 자체는 안전하다.
const linkifyHtml = (html) => {
    if (typeof DOMParser === "undefined" || html.search(URL_REGEX) === -1) {
        return html;
    }

    const doc = new DOMParser().parseFromString(html, "text/html");
    const walker = doc.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT);
    const textNodes = [];
    while (walker.nextNode()) textNodes.push(walker.currentNode);

    textNodes.forEach((node) => {
        if (node.parentElement?.closest("a")) return;
        const parts = node.nodeValue.split(URL_REGEX);
        if (parts.length === 1) return;

        const fragment = doc.createDocumentFragment();
        parts.forEach((part) => {
            if (!part) return;
            if (!/^(?:https?:\/\/|www\.)/i.test(part)) {
                fragment.appendChild(doc.createTextNode(part));
                return;
            }
            const { url, trailing } = splitTrailingPunctuation(part);
            const anchor = doc.createElement("a");
            anchor.href = /^www\./i.test(url) ? `https://${url}` : url;
            anchor.target = "_blank";
            anchor.rel = "noopener noreferrer";
            anchor.textContent = url;
            fragment.appendChild(anchor);
            if (trailing) fragment.appendChild(doc.createTextNode(trailing));
        });
        node.replaceWith(fragment);
    });

    return doc.body.innerHTML;
};

const TuiViewer = ({ images, htmlString, className }) => {
    const html = useMemo(
        () =>
            !!images
                ? makeHTMLWithImages(images)
                : linkifyHtml(normalizeEmptyParagraphs(htmlString || "")),
        [images, htmlString]
    );

    return (
        <div className={`tui-viewer ${className || ""}`}>
            <div
                className="ql-editor"
                dangerouslySetInnerHTML={{ __html: html }}
            />
        </div>
    );
};

export default TuiViewer;
